import { useState } from "react";
import { auth, db } from "./firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { allPlayerNames } from "./lib/players";

var inputStyle = {
  width: "100%", boxSizing: "border-box",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,165,0,0.2)",
  borderRadius: 8, color: "#fff",
  padding: "11px 14px", fontSize: 14,
  outline: "none", fontFamily: "sans-serif",
};

var btnStyle = {
  width: "100%", padding: "12px",
  background: "linear-gradient(135deg,#FFB020,#FF6B00)",
  border: "none", borderRadius: 8,
  color: "#000", fontWeight: 800,
  fontSize: 15, cursor: "pointer",
  fontFamily: "sans-serif", letterSpacing: 0.5,
};

var linkStyle = {
  background: "none", border: "none",
  color: "#FFB020", cursor: "pointer",
  fontSize: 13, textDecoration: "underline",
  fontFamily: "sans-serif", padding: 0,
};

export default function AuthPage({ onLogin }) {
  var [mode, setMode] = useState("login"); // login | register | reset
  var [email, setEmail] = useState("");
  var [password, setPassword] = useState("");
  var [firstName, setFirstName] = useState("");
  var [lastName, setLastName] = useState("");
  var [error, setError] = useState("");
  var [info, setInfo] = useState("");
  var [loading, setLoading] = useState(false);

  // Player linking step
  var [step, setStep] = useState("form"); // form | link
  var [matches, setMatches] = useState([]);
  var [selectedPlayer, setSelectedPlayer] = useState(null);

  function clearMessages() { setError(""); setInfo(""); }

  function handleLogin(e) {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    signInWithEmailAndPassword(auth, email, password)
      .then(function(cred) {
        return getDoc(doc(db, "users", cred.user.uid)).then(function(snap) {
          onLogin({ uid: cred.user.uid, email: cred.user.email, playerName: snap.exists() ? snap.data().playerName : null, isAdmin: snap.exists() ? !!snap.data().isAdmin : false });
        });
      })
      .catch(function(err) {
        setError(friendlyError(err.code));
      })
      .finally(function() { setLoading(false); });
  }

  function handleRegisterStep1(e) {
    e.preventDefault();
    clearMessages();
    if (!firstName.trim() || !lastName.trim()) { setError("Please enter your first and last name."); return; }
    var q = (lastName.trim() + ", " + firstName.trim()).toLowerCase();
    var found = allPlayerNames.filter(function(p) {
      return p.toLowerCase().indexOf(q) !== -1 ||
             (p.toLowerCase().indexOf(firstName.trim().toLowerCase()) !== -1 && p.toLowerCase().indexOf(lastName.trim().toLowerCase()) !== -1);
    });
    setMatches(found);
    // No historical results to link to — e.g. a new player who hasn't played an
    // EDC event yet. Let them create a fresh profile under the name they typed
    // rather than dead-ending the whole registration flow.
    setSelectedPlayer(found.length === 0 ? (lastName.trim() + ", " + firstName.trim()) : null);
    setStep("link");
  }

  function handleRegisterStep2(e) {
    e.preventDefault();
    clearMessages();
    if (!selectedPlayer) { setError("Please select your name from the list."); return; }
    if (!email || !password) { setError("Please enter your email and password."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    createUserWithEmailAndPassword(auth, email, password)
      .then(function(cred) {
        return setDoc(doc(db, "users", cred.user.uid), {
          email: cred.user.email,
          playerName: selectedPlayer,
          isAdmin: false,
          createdAt: new Date().toISOString(),
        }).then(function() {
          onLogin({ uid: cred.user.uid, email: cred.user.email, playerName: selectedPlayer, isAdmin: false });
        });
      })
      .catch(function(err) {
        setError(friendlyError(err.code));
      })
      .finally(function() { setLoading(false); });
  }

  function handleReset(e) {
    e.preventDefault();
    clearMessages();
    if (!email) { setError("Enter your email address first."); return; }
    setLoading(true);
    sendPasswordResetEmail(auth, email)
      .then(function() {
        setInfo("Password reset email sent — check your inbox.");
        setMode("login");
      })
      .catch(function(err) { setError(friendlyError(err.code)); })
      .finally(function() { setLoading(false); });
  }

  function friendlyError(code) {
    var msgs = {
      "auth/email-already-in-use": "That email is already registered. Try logging in instead.",
      "auth/invalid-email": "Please enter a valid email address.",
      "auth/wrong-password": "Incorrect password. Try again or reset your password.",
      "auth/user-not-found": "No account found with that email.",
      "auth/weak-password": "Password must be at least 6 characters.",
      "auth/invalid-credential": "Incorrect email or password.",
      "auth/too-many-requests": "Too many attempts. Please wait a moment and try again.",
    };
    return msgs[code] || "Something went wrong. Please try again.";
  }

  var card = {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,165,0,0.15)",
    borderRadius: 16, padding: "32px 28px",
    maxWidth: 440, margin: "0 auto", width: "100%",
  };

  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px", fontFamily: "sans-serif", color: "#fff" }}>
      <div style={card}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>{"🎯"}</div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1, background: "linear-gradient(135deg,#FFB020,#FF6B00)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            {mode === "login" ? "Player Login" : mode === "register" ? "Create Account" : "Reset Password"}
          </h2>
          {mode === "register" && step === "link" && (
            <p style={{ margin: "8px 0 0", fontSize: 13, color: "#666" }}>
              {matches.length > 0 ? "Select your name from the results data" : "New player — no results to link yet"}
            </p>
          )}
        </div>

        {error && <div style={{ background: "rgba(255,80,80,0.1)", border: "1px solid rgba(255,80,80,0.3)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#ff8080", marginBottom: 16 }}>{error}</div>}
        {info && <div style={{ background: "rgba(100,200,100,0.1)", border: "1px solid rgba(100,200,100,0.3)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#6DC878", marginBottom: 16 }}>{info}</div>}

        {mode === "login" && (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Email</div>
              <input style={inputStyle} type="email" value={email} onChange={function(e){ setEmail(e.target.value); }} placeholder="your@email.com" required />
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Password</div>
              <input style={inputStyle} type="password" value={password} onChange={function(e){ setPassword(e.target.value); }} placeholder="••••••••" required />
            </div>
            <button type="submit" style={btnStyle} disabled={loading}>{loading ? "Logging in..." : "Log In"}</button>
            <div style={{ textAlign: "center", marginTop: 16, display: "flex", justifyContent: "space-between" }}>
              <button type="button" style={linkStyle} onClick={function(){ setMode("reset"); clearMessages(); }}>Forgot password?</button>
              <button type="button" style={linkStyle} onClick={function(){ setMode("register"); setStep("form"); clearMessages(); }}>Create account</button>
            </div>
          </form>
        )}

        {mode === "register" && step === "form" && (
          <form onSubmit={handleRegisterStep1}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>First Name</div>
                <input style={inputStyle} value={firstName} onChange={function(e){ setFirstName(e.target.value); }} placeholder="Andy" />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Last Name</div>
                <input style={inputStyle} value={lastName} onChange={function(e){ setLastName(e.target.value); }} placeholder="Moore" />
              </div>
            </div>
            <button type="submit" style={btnStyle}>Find My Results</button>
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button type="button" style={linkStyle} onClick={function(){ setMode("login"); clearMessages(); }}>Already have an account?</button>
            </div>
          </form>
        )}

        {mode === "register" && step === "link" && (
          <form onSubmit={handleRegisterStep2}>
            <div style={{ marginBottom: 16 }}>
              {matches.length > 0 ? (
                <>
                  <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>{"Found " + matches.length + " match" + (matches.length === 1 ? "" : "es") + " — select your name:"}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 180, overflowY: "auto" }}>
                    {matches.map(function(p, pi) {
                      var active = selectedPlayer === p;
                      return (
                        <button type="button" key={pi} onClick={function(){ setSelectedPlayer(p); }} style={{ background: active ? "rgba(255,176,32,0.15)" : "rgba(255,255,255,0.04)", border: active ? "1px solid rgba(255,176,32,0.5)" : "1px solid rgba(255,255,255,0.08)", borderRadius: 8, color: active ? "#FFB020" : "#ccc", padding: "10px 14px", fontSize: 14, cursor: "pointer", textAlign: "left", fontWeight: active ? 700 : 400 }}>
                          {p}
                          {active && <span style={{ float: "right" }}>{"✓"}</span>}
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 13, color: "#888", background: "rgba(255,176,32,0.06)", border: "1px solid rgba(255,176,32,0.2)", borderRadius: 8, padding: "10px 14px" }}>
                  No existing results found for <span style={{ color: "#FFB020", fontWeight: 700 }}>{selectedPlayer}</span> — that's fine, you'll get a fresh profile. If that's a typo, search again below.
                </div>
              )}
              <button type="button" style={{ ...linkStyle, display: "block", marginTop: 8 }} onClick={function(){ setStep("form"); setMatches([]); setSelectedPlayer(null); }}>Search again</button>
            </div>
            {selectedPlayer && (
              <div>
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 16, marginBottom: 14 }}>
                  <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>{(matches.length > 0 ? "Linking as: " : "Creating profile as: ")}<span style={{ color: "#FFB020", fontWeight: 700 }}>{selectedPlayer}</span></div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Email</div>
                  <input style={inputStyle} type="email" value={email} onChange={function(e){ setEmail(e.target.value); }} placeholder="your@email.com" required />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Password</div>
                  <input style={inputStyle} type="password" value={password} onChange={function(e){ setPassword(e.target.value); }} placeholder="min. 6 characters" required />
                </div>
                <button type="submit" style={btnStyle} disabled={loading}>{loading ? "Creating account..." : "Create Account"}</button>
              </div>
            )}
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button type="button" style={linkStyle} onClick={function(){ setMode("login"); clearMessages(); setStep("form"); }}>Already have an account?</button>
            </div>
          </form>
        )}

        {mode === "reset" && (
          <form onSubmit={handleReset}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Email</div>
              <input style={inputStyle} type="email" value={email} onChange={function(e){ setEmail(e.target.value); }} placeholder="your@email.com" required />
            </div>
            <button type="submit" style={btnStyle} disabled={loading}>{loading ? "Sending..." : "Send Reset Email"}</button>
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button type="button" style={linkStyle} onClick={function(){ setMode("login"); clearMessages(); }}>Back to login</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
